/* eslint-disable @typescript-eslint/naming-convention */
import create from 'zustand';
import {
  Episode,
  Prompt,
  PromptComment,
  PromptState,
  EpisodeSection,
  PromptCollection,
  QuestionResponse,
  ResponseComment,
} from '../@types';
import * as Constants from '../constants';

type PromptActions = {
  setIsOpen: (promptId: string, isOpen: boolean) => void;
  setPrompt: (prompt: Prompt) => void;
  togglePromptComments: (promptId: string) => void;
  openResponse: (promptId: string, userId: string) => void;
  closeResponseViewer: (promptId: string) => void;
};

type ResponseActions = {
  // addResponseComment: (comment: ResponseComment) => void;
};
type EpisodeProgress = {
  current_section_id: EpisodeSection['id'] | null;
  current_timestamp?: number;
};
type EpisodeState = {
  episode: Episode | null;
  currentSection: EpisodeSection | null;
  sectionPrompts: PromptState[];
  progress: EpisodeProgress;
  showEndScreen: boolean;
  showDashboard: boolean;
  showSonar: boolean;
  player: any;
  getPromptStateById: (promptId: string) => PromptState;
  PROMPT_ACTIONS: PromptActions;
  EPISODE_ACTIONS: EpisodeActions;
  RESPONSE_ACTIONS: ResponseActions;
};

type EpisodeActions = {
  // setEpisode: (episode: EpisodeState) => void;
  setProgress: (current_section_id: string, current_timestamp: number) => void;
  setPlayer: (player: any) => void;
  goTimestamp: (timestamp: number, prompt_id: string | false) => void;
  playVideo: () => void;
  setShowEndScreen: (showEndScreen: boolean) => void;
  setShowDashboard: (showDashboard: boolean) => void;
  setShowSonar: (showSonar: boolean) => void;
  setPrompts: (prompts: PromptCollection) => void;
  initEpisode: (
    episode: Episode,
    current_section_id: string,
    current_timestamp: number
  ) => void;
};

const initialPromptState: PromptState = {
  prompt: null,
  isOpen: false,
  commentsOpen: false,
  responsesOpen: false,
  currentResponse: null,
  answered: false,
};

const getPrompt = (promptId: string, prompts: PromptState[]) => {
  const idx = prompts.findIndex((p) => p.prompt && p.prompt.id === promptId);
  return { idx, state: prompts[idx] };
};
const getResponse = (responseId: string, responses: QuestionResponse[]) => {
  const idx = responses.findIndex((r) => r.id === responseId);
  return idx >= 0 ? { idx, response: responses[idx] } : false;
};

const useEpisodeStore = create<EpisodeState>((set, get) => ({
  episode: null,
  currentSection: null,
  sectionPrompts: [],
  player: null,
  progress: {
    current_section_id: null, // 'abc',
    current_timestamp: 0,
  },
  showEndScreen: false,
  showDashboard: false,
  showSonar: false,
  getPromptStateById: (promptId: string) => {
    const { sectionPrompts } = get();
    const { state } = getPrompt(promptId, sectionPrompts);
    return state;
  },
  PROMPT_ACTIONS: {
    togglePromptComments: (promptId: string) => {
      const { sectionPrompts, player } = get();
      const { idx, state } = getPrompt(promptId, sectionPrompts);
      state.commentsOpen = !state.commentsOpen;
      sectionPrompts[idx] = state;
      set({ sectionPrompts });
      if (state.commentsOpen) {
        player.pause();
      }
    },
    closeResponseViewer: (promptId) => {
      const { sectionPrompts } = get();
      const { idx, state } = getPrompt(promptId, sectionPrompts);
      state.responsesOpen = false;
      sectionPrompts[idx] = state;
      set({ sectionPrompts });
    },
    openResponse: (promptId: string, userId: string) => {
      const { sectionPrompts } = get();
      const { idx, state } = getPrompt(promptId, sectionPrompts);
      state.responsesOpen = true;
      state.currentResponse = [promptId, userId];
      sectionPrompts[idx] = state;
      set({ sectionPrompts });
    },
    setIsOpen: (promptId: string, isOpen: boolean) => {
      const { sectionPrompts } = get();
      const { idx } = getPrompt(promptId, sectionPrompts);
      sectionPrompts[idx].isOpen = isOpen;
      set({ sectionPrompts: [...sectionPrompts] });
    },
    setPrompt: (p) => {
      const { sectionPrompts } = get();
      const { idx } = getPrompt(p.id, sectionPrompts);
      if (idx) {
        sectionPrompts[idx].prompt = p;
        set({ sectionPrompts });
      }
    },
  },
  RESPONSE_ACTIONS: {},

  EPISODE_ACTIONS: {
    setPlayer: (player: any) => {
      set({ player });
    },
    goTimestamp: (timestamp: number, prompt_id: string | false = false) => {
      const { player, PROMPT_ACTIONS, sectionPrompts } = get();
      player.currentTime(timestamp);
      if (prompt_id) {
        sectionPrompts.forEach((sp) => {
          if (sp.prompt) {
            PROMPT_ACTIONS.setIsOpen(sp.prompt.id, false);
          }
        });
        PROMPT_ACTIONS.setIsOpen(prompt_id, true);
      }
    },
    playVideo: () => {
      const { player } = get();
      setTimeout(() => {
        player.play();
      }, 200);
    },
    setProgress: (current_section_id, current_timestamp) => {
      // console.log('updating progress', current_timestamp);
      set({ progress: { current_section_id, current_timestamp } });
    },
    setShowEndScreen: (showEndScreen) => set({ showEndScreen }),
    setShowDashboard: (showDashboard) => set({ showDashboard }),
    setShowSonar: (showSonar) => set({ showSonar }),
    setPrompts: (prompts: PromptCollection) => {
      const { sectionPrompts: previousState } = get();

      let sectionPrompts = [] as PromptState[];
      if (prompts) {
        sectionPrompts = Object.entries(prompts).map(([timestamp, prompt]) => {
          const promptState: any = previousState.length
            ? previousState.filter((p) => p?.prompt?.id === prompt.id)[0]
            : initialPromptState;
          return {
            ...promptState,
            prompt,
          };
        });
        set({ sectionPrompts });
      }
    },
    initEpisode: async (episode, current_section_id, current_timestamp) => {
      // Initialize Current Section Store
      const currentSection = current_section_id
        ? episode.sections.filter(
            (section) => section.id === current_section_id
          )[0]
        : episode.sections[0];

      // Set Episode State
      set({
        ...get(),
        episode,
        currentSection,
        progress: { current_section_id, current_timestamp },
      });
    },
  },
}));
export { useEpisodeStore };
